/**
 * Saved Searches utilities.
 *
 * Saved searches are stored in user.attributes.protectedData.savedSearches[]
 * as an array of search objects:
 *   {
 *     id: string (UUID),
 *     name: string,
 *     query: Object (search params: keywords, location, filters, etc.),
 *     createdAt: ISO date string,
 *     notifyByEmail: boolean
 *   }
 */

import { v4 as uuidv4 } from 'uuid';

/**
 * Get all saved searches for a user.
 */
export const getSavedSearches = user =>
  user?.attributes?.protectedData?.savedSearches || [];

/**
 * Create a new saved search object.
 */
export const createSavedSearch = (name, query, notifyByEmail = false) => ({
  id: uuidv4(),
  name: name.trim(),
  query,
  createdAt: new Date().toISOString(),
  notifyByEmail,
});

/**
 * Add a saved search to the list.
 */
export const addSavedSearch = (user, savedSearch) => {
  const current = getSavedSearches(user);
  return { savedSearches: [...current, savedSearch] };
};

/**
 * Remove a saved search by ID.
 */
export const removeSavedSearch = (user, searchId) => {
  const current = getSavedSearches(user);
  return { savedSearches: current.filter(s => s.id !== searchId) };
};

/**
 * Build the search URL from a saved search query object.
 * @param {Object} query — search params
 * @returns {string} — relative URL like /s?keywords=bike&pub_category=city
 */
export const buildSearchUrl = query => {
  if (!query) return '/s';
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.set(key, value);
    }
  });
  const qs = params.toString();
  return qs ? `/s?${qs}` : '/s';
};
